import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Search, RefreshCw, Phone, Mail, Copy, KeyRound, Lock } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "citycivils_admin_token";

function LoginView({ onAuth }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    try {
      await axios.get(`${API}/admin/contacts`, {
        headers: { "x-admin-token": token.trim() },
      });
      localStorage.setItem(TOKEN_KEY, token.trim());
      onAuth(token.trim());
    } catch (err) {
      toast.error("Invalid admin token");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-[#0B0B0B] border border-[#181818] p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 inline-flex items-center justify-center bg-[#F28C00]">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-[#F28C00] font-display font-bold uppercase tracking-[0.2em] text-[11px]">Admin Area</p>
            <h1 className="font-display font-black uppercase text-white text-2xl leading-none mt-1">Sign In</h1>
          </div>
        </div>
        <label className="block text-[#9A9A9A] uppercase tracking-[0.2em] text-[11px] font-display font-bold mb-2">Admin Token</label>
        <div className="relative">
          <KeyRound className="w-4 h-4 text-[#5a5a5a] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="password"
            autoFocus
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste your admin token"
            className="cc-input pl-11"
            data-testid="admin-token-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !token.trim()}
          className="cc-btn-primary w-full mt-6 disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {busy ? "Verifying..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Admin() {
  useSEO("Admin");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const fetchList = async (tk) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: { "x-admin-token": tk } });
      setItems(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please sign in again");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchList(token);
  }, [token]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((i) =>
      [i.full_name, i.email, i.phone, i.service, i.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle))
    );
  }, [items, q]);

  const selected = filtered.find((i) => i.id === selectedId) || filtered[0] || null;

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed")
    );
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
    setSelectedId(null);
  };

  if (!token) return <LoginView onAuth={setToken} />;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="cc-eyebrow mb-3">Admin</p>
            <h1 className="font-display font-black uppercase text-white text-3xl md:text-4xl leading-none">Contact Submissions</h1>
            <p className="text-[#9A9A9A] mt-2 text-sm">{items.length} total · {filtered.length} shown</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchList(token)}
              className="cc-btn-outline"
              data-testid="admin-refresh"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="cc-btn-primary" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="w-4 h-4 text-[#5a5a5a] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone, service or message..."
            className="cc-input pl-11"
            data-testid="admin-search"
          />
        </div>

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div className="bg-[#0B0B0B] border border-[#181818] p-16 text-center">
            <p className="text-[#9A9A9A] text-sm">
              {items.length === 0
                ? "No submissions yet. When someone fills the contact form it will appear here."
                : "No matches for your search."}
            </p>
          </div>
        )}

        {/* Two-pane layout */}
        {filtered.length > 0 && (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
            {/* List */}
            <div className="bg-[#0B0B0B] border border-[#181818] max-h-[70vh] overflow-y-auto">
              {filtered.map((it) => {
                const active = (selected?.id) === it.id;
                return (
                  <button
                    key={it.id}
                    onClick={() => setSelectedId(it.id)}
                    className={`w-full text-left px-5 py-4 border-b border-[#181818] last:border-b-0 transition-colors ${
                      active ? "bg-[#111] border-l-[3px] border-l-[#F28C00]" : "hover:bg-[#111]"
                    }`}
                    data-testid={`admin-item-${it.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-white font-display font-bold uppercase tracking-wider text-sm truncate">
                        {it.full_name}
                      </span>
                      <span className="text-[#666] text-[11px] shrink-0">{fmtDate(it.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] uppercase tracking-[0.18em] font-display font-bold text-[#F28C00]">{it.service}</span>
                      <span className="text-[#333]">·</span>
                      <span className="text-[#9A9A9A] text-xs truncate">{it.email}</span>
                    </div>
                    <p className="text-[#9A9A9A] text-xs mt-1.5 line-clamp-2">{it.message}</p>
                  </button>
                );
              })}
            </div>

            {/* Detail */}
            {selected && (
              <div className="bg-[#0B0B0B] border border-[#181818] p-6 lg:p-8 max-h-[70vh] overflow-y-auto" data-testid="admin-detail">
                <div className="flex items-start justify-between gap-4 mb-5 pb-5 border-b border-[#181818]">
                  <div className="min-w-0">
                    <h2 className="font-display font-black uppercase text-white text-2xl leading-tight truncate">{selected.full_name}</h2>
                    <p className="text-[#F28C00] font-display font-bold uppercase tracking-[0.18em] text-[11px] mt-1.5">{selected.service}</p>
                    <p className="text-[#666] text-xs mt-1">{fmtDate(selected.created_at)}</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 flex-wrap">
                    <a href={`tel:${selected.phone}`} className="cc-btn-outline text-xs px-4 py-2.5">
                      <Phone className="w-3.5 h-3.5" /> {selected.phone}
                    </a>
                    <button onClick={() => copy(selected.phone)} className="text-[#666] hover:text-[#F28C00] transition-colors" title="Copy">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <a href={`mailto:${selected.email}`} className="cc-btn-outline text-xs px-4 py-2.5">
                      <Mail className="w-3.5 h-3.5" /> {selected.email}
                    </a>
                    <button onClick={() => copy(selected.email)} className="text-[#666] hover:text-[#F28C00] transition-colors" title="Copy">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-[#9A9A9A] uppercase tracking-[0.2em] text-[11px] font-display font-bold mb-3">Message</p>
                  <div className="bg-[#050505] border-l-[3px] border-[#F28C00] p-5 text-white text-sm leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
